<template>
  <BaseDialog
    data-testid="edit-reservation-advance-settings-modal"
    width="566px"
    title="編輯進階服務設定"
    :btn1Loading="loading"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <div>
      <BaseElForm
        :model="formData"
        label-position="top"
        @submit.prevent.native="updateReservationParameter"
      >
        <BaseElFormItem label="預約模式" class="mb-[32px]">
          <BaseElRadioGroup v-model="formData.clientOrderMode" testName="formData_clientOrderMode">
            <div class="flex flex-col" style="gap: 20px">
              <BaseElRadio
                v-for="option in displayAppointmentModeOptions"
                :key="option.value"
                class="flex items-start"
                :label="option.value"
              >
                <div class="flex flex-col" style="gap: 8px">
                  <p class="text-sub font-medium">{{ option.label }}</p>
                  <p class="text-sm font-normal">{{ option.description }}</p>
                </div>
              </BaseElRadio>
            </div>
          </BaseElRadioGroup>
        </BaseElFormItem>

        <BaseElFormItem label="多人預約單服務">
          <BaseElSwitch
            v-model="formData.allowOrderMorePeople"
            testName="formData_allowOrderMorePeople"
            class="switch-input"
            active-text="開啟"
            inactive-text="關閉"
          />
        </BaseElFormItem>

        <BaseElFormItem v-if="showControl.formItem.batchOrderNotifyMode" label="LINE 建立預約通知">
          <BaseElRadioGroup v-model="formData.batchOrderNotifyMode" testName="formData_batchOrderNotifyMode">
            <div class="flex flex-col" style="gap: 12px">
              <BaseElRadio label="merge">
                <div class="inline-flex flex-col text-sub w-full" style="gap: 8px">
                  <p class="text-gray-100">合併為一則建立預約通知</p>
                  <div class="text-sm text-gray-60">建立多個預約時段時，僅發送一則建立通知訊息卡片，後續各預約通知<br>仍獨立發送</div>
                </div>
              </BaseElRadio>
              <BaseElRadio label="independent">
                <div class="inline-flex flex-col text-sub" style="gap: 8px">
                  <p class="text-gray-100">獨立所有時段的預約通知</p>
                  <p class="text-sm text-gray-60">每一個時段都會發送獨立的通知訊息卡片，請注意當建立多個預約時段時<br>客人會同時收到多則通知</p>
                </div>
              </BaseElRadio>
            </div>
          </BaseElRadioGroup>
        </BaseElFormItem>

        <BaseElFormItem
          v-if="formData.allowOrderMorePeople"
          label="多人預約人數上限"
        >
          <BaseElInputNumber
            v-model="formData.maxOrderMorePeopleCount"
            testName="formData_maxOrderMorePeopleCount"
            placeholder="請輸入人數"
            :min="1"
            :max="10"
          />
        </BaseElFormItem>

        <template v-if="['otherReservation'].includes(formData.clientOrderMode)">
          <!-- <BaseElFormItem label="單人預約多服務">
            <BaseElSwitch
              v-model="formData.allowOtherReservation"
              data-cy="allow-order-other-service-switch"
              class="switch-input"
              active-text="開啟"
              inactive-text="關閉"
            />
          </BaseElFormItem> -->

          <template>
            <BaseElFormItem label="其他服務預設開始時間設定">
              <GroupOptionsButton
                testName="formData_secondOrderStartDefault"
                :model.sync="formData.secondOrderStartDefault"
                :options="[
                  {label: '接續第一個服務', value: 'continue'},
                  {label: '與第一個服務同時', value: 'same'},
                  {label: '顧客自行選擇', value: 'client'},
                ]"
              />
              <p v-if="formData.secondOrderStartDefault === 'client'" class="text-danger text-sm mt-[12px] leading-[16px]">
                此模式下系統僅檢查『預約空檔』，無法自動判別多項服務組合的人力或設備配置（例如：現場場地是否支援多項療程同時進行）。若顧客預約內容超出實際場地限制，需由店家手動聯繫顧客調整。
              </p>
            </BaseElFormItem>
            <!-- <BaseElFormItem>
                <template slot="label">
                  <div class="flex items-center">
                    <span style="margin-right: 2px">是否開放客人自選其他服務時間</span>
                    <el-tooltip placement="right">
                      <div slot="content">
                        開放自選時，系統會自動預設時間，而客人可根據需求自行選擇其他時間選項<br>
                        關閉自選時，系統會自動預設時間，而客人不可更改時間選項
                      </div>
                      <span class="material-icons">
                        help_outline
                      </span>
                    </el-tooltip>
                  </div>
                </template>
                <BaseElSwitch
                  v-model="formData.secondOrderStartClientChange"
                  active-text="開啟"
                  inactive-text="關閉"
                  @change="updateReservationParameter"
                />
              </BaseElFormItem> -->
          </template>
        </template>
      </BaseElForm>
    </div>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, onBeforeMount } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useBaseForm } from '@/use/useForm'
import { UpdateReservationParameter } from '@/api/reservation'
import GroupOptionsButton from '@/components/Button/GroupOptionsButton.vue'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'

export default defineComponent({
  name: 'EditReservationAdvanceSettings',
  components: {
    BaseDialog,
    GroupOptionsButton,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formRef, formData, loading, initFormData } = useBaseForm()
    const { checkAction } = usePermissions()

    initFormData({
      clientOrderMode: 'normal', // normal | otherReservation | batchOrder
      allowBatchOrder: false,
      allowOrderMorePeople: false,
      maxOrderMorePeopleCount: 1,
      allowOtherReservation: false,
      secondOrderStartDefault: 'continue',
      secondOrderStartClientChange: false,
      batchOrderNotifyMode: 'independent',
    })

    const displayAppointmentModeOptions = computed(() => {
      const allOptions = [
        { label: '基礎模式', description: '基本預約模式，一次預約一個服務', value: 'normal' },
        { label: '多服務模式', description: '可一次預約多個服務，最多一次可預約 5 個服務', value: 'otherReservation' },
        { label: '多時段模式', description: '可一次預約同個服務的多個時段，最多一次可預約 4 個時段', value: 'batchOrder' },
      ]

      // 當啟用 KeePer 客製專案且收費模式為定金或預約結帳時，隱藏多時段模式
      const shouldHideBatchOrder = checkAction('planModule.keeper.enabled') &&
        ['deposit', 'checkout'].includes(props.configData.chargeType)

      // 進階功能需具備權限才能顯示「多服務模式」與「多時段模式」
      const canEditAdvanced = checkAction('admin.appointmentConfig.editAdvancedFeatures')

      let options = allOptions
      if (!canEditAdvanced) {
        options = options.filter(option => !['otherReservation', 'batchOrder'].includes(option.value))
      }

      if (shouldHideBatchOrder) {
        options = options.filter(option => option.value !== 'batchOrder')
      }

      return options
    })

    const showControl = computed(() => {
      const controls = {
        formItem: {
          batchOrderNotifyMode: false,
        },
      }

      if (formData.clientOrderMode === 'batchOrder') {
        controls.formItem.batchOrderNotifyMode = true
      }

      return controls
    })

    const updateReservationParameter = async () => {
      if (loading.value) return
      loading.value = true

      const [, err] = await UpdateReservationParameter({
        shopId: shopId.value,
        clientOrderMode: formData.clientOrderMode,
        allowOrderMorePeople: formData.allowOrderMorePeople,
        maxOrderMorePeopleCount: formData.maxOrderMorePeopleCount || 1,
        // allowOtherReservation: formData.allowOtherReservation,
        // secondOrderStartClientChange: formData.secondOrderStartClientChange,
        secondOrderStartDefault: formData.secondOrderStartDefault,
        // allowBatchOrder: formData.allowBatchOrder,
        batchOrderNotifyMode: formData.batchOrderNotifyMode,
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success('更新成功!')
      emit('refresh')
      emit('close')
    }

    const canEditAdvanced = checkAction('admin.appointmentConfig.editAdvancedFeatures')

    const syncFormData = () => {
      const config = { ...props.configData }
      formData.clientOrderMode = canEditAdvanced ? config.clientOrderMode : 'normal'
      // formData.allowBatchOrder = config.allowBatchOrder
      formData.allowOrderMorePeople = config.allowOrderMorePeople
      formData.maxOrderMorePeopleCount = config.maxOrderMorePeopleCount || 1
      formData.allowOtherReservation = config.allowOtherReservation
      formData.secondOrderStartDefault = config.secondOrderStartDefault || 'continue'
      formData.secondOrderStartClientChange = config.secondOrderStartClientChange
      formData.batchOrderNotifyMode = config.batchOrderNotifyMode || 'independent'
    }

    const onConfirm = () => {
      updateReservationParameter()
    }
    onBeforeMount(() => {
      syncFormData()
    })

    return { onConfirm, formRef, formData, loading, updateReservationParameter, showControl, checkAction, displayAppointmentModeOptions }
  },
})

</script>

<style lang="postcss" scoped>
.area-disabled {
  @apply pointer-events-none opacity-50;
  filter: grayscale(1);
}
</style>
